import { forwardRef, Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { DependencyTreeCanActivate } from 'app/core/guards/dependency-tree.guard';
import { LoadState } from 'app/core/interfaces/generic.interface';
import { ActiveCustomerService } from 'app/core/services/active-customer.service';
import { GetDefinitionsGuard } from 'app/core/store/definitions/definitions.resolve';
import { AuthorizationMatrix } from 'app/core/store/session/authorization/authorization.interface';
import { Observable } from 'rxjs';
import * as fromRoot from '../store';
import { BaseGuard } from './base.guard';
import { map } from 'rxjs/operators';
import { UserContextLoadGuard } from './user-context-load.guard';

interface StateDate {
  authorizationState: LoadState<AuthorizationMatrix>;
  isSuperUser: boolean;
}

@Injectable({ providedIn: 'root' })
export class CustomerPermissionsLoadIfSuperUserGuard extends BaseGuard<StateDate> implements DependencyTreeCanActivate {
  private store: Store<fromRoot.RootState>;
  private activeCustomerService: ActiveCustomerService;

  constructor(@Inject(forwardRef(() => Store)) store, @Inject(forwardRef(() => ActiveCustomerService)) activeCustomerService) {
    super('CustomerPermissionsLoadIfSuperUserGuard');
    this.store = store;
    this.activeCustomerService = activeCustomerService;
  }

  getState(): Observable<StateDate> {
    return this.store.pipe(
      map(state => {
        return {
          authorizationState: state.session.authorization,
          isSuperUser: state.session.userContext.isSuperUser,
        };
      })
    );
  }

  hasFailed(t: StateDate): boolean {
    return t.authorizationState.loadFailed;
  }

  isLoaded(t: StateDate): boolean {
    return !t.isSuperUser || t.authorizationState.loadSuccess;
  }

  isLoading(t: StateDate): boolean {
    return t.authorizationState.loading;
  }

  loader(t: StateDate, params: { [p: string]: string }): void {
    this.activeCustomerService.loadCustomerPermissions();
  }

  resetter(params: { [p: string]: string }): void {
    // Not applicable
  }

  versionChecker(t: StateDate, params: { [p: string]: string }): boolean {
    return true; // there is only 1 possible value
  }

  navigateAway(): void {
    // Not applicable
  }

  getDependencies() {
    return [GetDefinitionsGuard, UserContextLoadGuard];
  }

  getDependencyRouteData() {
    return null; // Use original route-data
  }

  didntChange(v1: StateDate, v2: StateDate) {
    return (
      v1.authorizationState.loadSuccess === v2.authorizationState.loadSuccess &&
      v1.authorizationState.loading === v2.authorizationState.loading &&
      v1.authorizationState.loadFailed === v2.authorizationState.loadFailed &&
      v1.isSuperUser === v2.isSuperUser
    );
  }
}
